import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import { Button, Icon, Message } from 'semantic-ui-react';
import { setPage } from '../../redux/appSlice';
import Step1NotificationLetter from '../PdfLetters/Step1NotificationLetter';
import axios from 'axios';
import { setLetter } from "../../redux/projectSlice";


const useTitle = (title) => {
    useEffect(() => {
        const prevTitle = document.title;
        document.title = title;
        return () => {
            document.title = prevTitle;
        }
    })
}

const Step1NotificationLetterService = () => {

    const projectState = useSelector(state => state.project);
    const appState = useSelector(state => state.app);
    const projectChecklists = useSelector(state => state.project.projectChecklists);
    const dispatch = useDispatch();
    const componentRef = useRef();
    const API_MDB = process.env.REACT_APP_BASE_API_URL_MDB;
    const options = {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
    }
    const handlePrint = useReactToPrint({
        content: () => componentRef.current
    });

    const updateLetterDataAndPrint = async () => {
        const resLetter = await axios.patch(API_MDB + '/letters/' + projectState.letter._id, projectState.letter, options);
        if (resLetter.status === 204) {
            handlePrint();
        }
    }


    const displayError = (error, title, message) => {
        return (<Message negative><Message.Header>{title}</Message.Header>{message}</Message>);
    }

    const getDecision = () => {
        const project = projectState.project
        if (project.firstStepDecision !== null) {
            if (project.firstStepDecision.finalFundingDecision !== null) {
                if (project.firstStepDecision.finalFundingDecision.status === 'STEP1_APPROVED_WITH_CONDITIONS' || project.firstStepDecision.finalFundingDecision.status === 'STEP1_APPROVED') {
                    return 'APPROVAL'
                } else {
                    return 'REJECTION'
                }
            }
            if (project.firstStepDecision.eligibilityDecision !== null) {
                if (project.firstStepDecision.eligibilityDecision.status === 'STEP1_ELIGIBLE') {
                    return 'ELIGIBLE'
                } else {
                    return (
                        'INELIGIBLE'
                    )
                }
            } else {
                return displayError(null, 'There is no Step1 Decision at the moment', 'It\'s not possible to print a notification letter without any decision, please check in Jems application if the Step1 decision is available');
            }
        } else {
            return displayError(null, 'There is no Step1 Decision at the moment', 'It\'s not possible to print a notification letter without any decision, please check in Jems application if the Step1 decision is available');
        }
    }

    const getLetterData = async () => {
        if (!projectState.letter.projectNumber) {
            const resLetter = await axios.get(API_MDB + '/letters/' + projectState.project.customIdentifier, options);
            if (resLetter.status === 200) {
                let newLetter = {};
                newLetter.projectNumber = projectState.project.customIdentifier;
                newLetter.callNumber = projectState.project.callSettings.callId;
                const jsOpinion = getJsOpinion();

                let letter = resLetter.data
                if (letter.projectNumber !== undefined) {
                    if ((letter.step1Data.additional === '' || letter.step1Data.additional === null) && getDecision() === 'APPROVAL') {
                        if (jsOpinion.details[4].explanation === '' || jsOpinion.details[4].explanation === undefined) {
                            letter.step1Data.additional = projectState.defaultAdditionalComment[1];
                        } else {
                            letter.step1Data.additional = jsOpinion.details[4].explanation
                        }
                    }
                    dispatch(setLetter(letter));
                } else {
                    let step1Data = {};
                    if (jsOpinion !== undefined) {
                        step1Data.projectNeed = jsOpinion.details[0].explanation;
                        step1Data.objectiveContribution = jsOpinion.details[1].explanation
                        step1Data.addedValueOfProgrammApproch = jsOpinion.details[2].explanation;
                        step1Data.partnershipRelevance = jsOpinion.details[3].explanation;
                        step1Data.additional = jsOpinion.details[4].explanation;
                        if (step1Data.additional === '' && getDecision() === 'APPROVAL') {
                            step1Data.additional = projectState.defaultAdditionalComment[1];
                        }
                    }
                    newLetter.step1Data = step1Data;
                    const resCommission = await axios.get(API_MDB + '/commissions/' + projectState.project.callSettings.callId, options);
                    if (resCommission.status === 200) {
                        newLetter.commission = resCommission.data;
                        if (projectState.letter.projectNumber === undefined) {
                            dispatch(setLetter(newLetter));
                            axios.post(API_MDB + '/letters', newLetter, options).then(res => {
                                dispatch(setLetter(res.data));
                            }).catch(err => {
                                console.log(err);
                            });
                        }
                    } else {

                        console.warn('Error fetching commission data');
                    }
                }
            } else {
                console.warn('Error fetching letter data');
            }
        }
    }

    const getChecklistByName = (name) => {
        let checklist = undefined;
        const checklists = projectState.projectChecklists;
        try {
            for (let i = 0; i < checklists.length; i++) {
                if (checklists[i].name === name) {
                    checklist = checklists[i];
                    return checklist;
                }
            }
        } catch (error) {
            return undefined
        }
    }

    const getJsOpinion = () => {
        const jsOpinionChecklistName = projectState.step1JSOpinion;
        const jsOpinionChecklist = getChecklistByName(jsOpinionChecklistName);
        if (jsOpinionChecklist === undefined) {
            return undefined;
        }
        let jsOpinionComponents = jsOpinionChecklist.detail.components;
        let sortedComponents = jsOpinionComponents.slice().sort((a, b) => a.position - b.position);
        let jsOpinion = {};
        jsOpinion.details = [];
        jsOpinion.summary = sortedComponents[1].instanceMetadata.explanation;
        jsOpinion.opinion = sortedComponents[2].instanceMetadata.answer;
        jsOpinion.opinionDetail = sortedComponents[2].instanceMetadata.justification;
        for (let i = 3; i < sortedComponents.length; i++) {
            if (sortedComponents[i].type === 'TEXT_INPUT') {
                let detail = {};
                detail.question = sortedComponents[i].programmeMetadata.question;
                detail.explanation = sortedComponents[i].instanceMetadata.explanation;
                jsOpinion.details.push(detail);
            }
        }
        return jsOpinion;
    }


    getLetterData();
    const letterTitle = 'P' + projectState.project.programmePriority.code + '_' + projectState.project.customIdentifier + '_' + projectState.project.acronym + '_S1_' + getDecision();
    useTitle(letterTitle);
    return (
        <div>
            <div className='menu'>
                <Button.Group>
                    <Button icon onClick={() => dispatch(setPage('Projects'))} color='blue' labelPosition='left'>
                        <Icon name='arrow left' />
                        Back
                    </Button>
                    <Button.Or />
                    <Button icon onClick={updateLetterDataAndPrint} color='blue' labelPosition='right'>
                        <Icon name='save' />
                        Save / Print
                    </Button>
                </Button.Group>

            </div>
            {appState.jems32 ? <Message warning><Message.Header>You're connected to Jems32 server.</Message.Header>Collected data may not reflect the live server please use <a href="http://jemsreports.nweurope.eu/" target="_blank" rel="noopener noreferrer">Jems Live Server</a></Message> : undefined}
            <Step1NotificationLetter ref={componentRef} projectState={projectState} projectChecklists={projectChecklists} />
        </div>
    );
}

export default Step1NotificationLetterService;